import { z } from "zod"
import { coreTextLimits } from "@ibada/shared-types/common"
import { currencyScale, isMoneyString } from "@ibada/shared-types/finance"

import { isNegativeMoney, toMoneyOrNull } from "@/features/finance/lib/finance-utils"
import type { FormTranslate } from "@/features/finance/schemas/finance-forms"
import { FinanceAccountType, FinanceCategoryScope, type MoneyText } from "@/features/finance/model/finance-types"

/**
 * An OPTIONAL money field: `""` means "not stated", which is not the same as zero.
 *
 * Both callers below used to validate money with floats (`Number.isFinite(Number(value))`) and then hand a
 * `number` to the API. Money now leaves as a canonical decimal string, so the field validates on minor units and
 * emits the string itself.
 *
 * @param allowNegative opening balances only: an overdrawn account is a real balance (IBA-120), and the old
 *        `openingNum >= 0` guard SILENTLY DROPPED a negative one — the form said saved, the API was never told.
 */
function buildOptionalMoneyFieldSchema(
  currency: string,
  t: FormTranslate,
  messages: { invalid: string; tooPrecise: string },
  options: { allowNegative: boolean },
) {
  return z
    .string()
    .trim()
    .transform((value, ctx): MoneyText | "" => {
      const fail = (message: string) => {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message })
        return z.NEVER
      }

      if (!value) return ""
      if (!isMoneyString(value)) return fail(t(messages.invalid))
      if (!options.allowNegative && isNegativeMoney(value)) return fail(t(messages.invalid))

      const money = toMoneyOrNull(value, currency)
      if (money === null) return fail(t(messages.tooPrecise, { decimals: currencyScale(currency) }))

      return money
    })
}

/** @param currency the branch's accounting currency — it decides the scale an opening balance may carry. */
export function buildFinanceAccountFormSchema(t: FormTranslate, currency: string) {
  return z.object({
    name: z.string().trim().min(2, t("validation.nameMin")).max(coreTextLimits.financeAccountName, t("validation.nameMax")),
    description: z.string().trim().max(coreTextLimits.financeDescription, t("validation.descriptionMax")).or(z.literal("")),
    // Optional last 4 digits of the real account number — a display-only reference aid. Absent/empty, or exactly 4 digits.
    accountNumberLast4: z
      .string()
      .trim()
      .regex(/^\d{4}$/, t("validation.accountNumberLast4"))
      .or(z.literal(""))
      .optional(),
    // How the money is physically held (IBA-114). The account's CURRENCY is deliberately absent: it is inherited
    // from the branch's accounting currency, because an account in any other currency could never be posted to.
    type: z.nativeEnum(FinanceAccountType),
    // Optional opening balance (IBA-71) — a canonical decimal string, sent as one (IBA-116). NEGATIVE IS VALID.
    openingBalance: buildOptionalMoneyFieldSchema(
      currency,
      t,
      { invalid: "validation.openingBalanceInvalid", tooPrecise: "validation.openingBalanceTooPrecise" },
      { allowNegative: true },
    ),
  })
}

export type FinanceAccountFormValues = z.input<ReturnType<typeof buildFinanceAccountFormSchema>>
export type FinanceAccountFormInput = z.output<ReturnType<typeof buildFinanceAccountFormSchema>>

export function createDefaultFinanceAccountFormValues(): FinanceAccountFormValues {
  return {
    name: "",
    description: "",
    accountNumberLast4: "",
    type: FinanceAccountType.Bank,
    openingBalance: "",
  }
}

/** How a fund's money may be spent, as the form asks it. Saved as `isRestricted` (Selected) or not (Any). */
export enum FundSpendingMode {
  /** For every expense: no restriction. */
  AnyExpense = "any_expense",
  /** Only some expenses: the chosen expense accounts and/or a period. */
  SelectedExpenses = "selected_expenses",
}

/** @param currency the branch's accounting currency — it decides the scale a fund target may carry. */
export function buildFinanceFundFormSchema(t: FormTranslate, currency: string) {
  return z
    .object({
      name: z
        .string()
        .trim()
        .min(2, t("validation.nameMin"))
        .max(coreTextLimits.financeAccountName, t("validation.nameMax")),
      description: z
        .string()
        .trim()
        .max(coreTextLimits.financeDescription, t("validation.descriptionMax"))
        .or(z.literal("")),
      // A fund's goal is money: a canonical decimal string, or `""` for "no goal" (IBA-116). The
      // `Number.isFinite(Number(value)) && Number(value) >= 0` refinement this replaces accepted `"1e3"` and
      // `"10.005"` — a target the API now refuses with a 422 the user could not have predicted.
      targetAmount: buildOptionalMoneyFieldSchema(
        currency,
        t,
        { invalid: "validation.targetAmountInvalid", tooPrecise: "validation.targetAmountTooPrecise" },
        { allowNegative: false },
      ),
      startDate: z.string().trim(),
      endDate: z.string().trim(),
      // Restricted Funds config: "Seulement certaines dépenses" is `isRestricted`; the period only counts when its
      // switch is on.
      spendingMode: z.nativeEnum(FundSpendingMode),
      allowedExpenseAccountIds: z.array(z.string()),
      hasRestrictionPeriod: z.boolean(),
      restrictionStartDate: z.string().trim(),
      restrictionEndDate: z.string().trim(),
    })
    .superRefine((values, context) => {
      if (values.startDate && values.endDate && values.endDate < values.startDate) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["endDate"],
          message: t("validation.endBeforeStart"),
        })
      }
      if (values.spendingMode !== FundSpendingMode.SelectedExpenses) return
      const periodDates = values.hasRestrictionPeriod && Boolean(values.restrictionStartDate || values.restrictionEndDate)
      if (values.hasRestrictionPeriod && !periodDates) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["restrictionStartDate"],
          message: t("validation.restrictionPeriodEmpty"),
        })
      } else if (values.allowedExpenseAccountIds.length === 0 && !periodDates) {
        // "Seulement certaines dépenses" with nothing chosen would restrict nothing at all.
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["allowedExpenseAccountIds"],
          message: t("validation.restrictionEmpty"),
        })
      }
      if (
        values.hasRestrictionPeriod &&
        values.restrictionStartDate &&
        values.restrictionEndDate &&
        values.restrictionEndDate < values.restrictionStartDate
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["restrictionEndDate"],
          message: t("validation.endBeforeStart"),
        })
      }
    })
}

export type FinanceFundFormValues = z.input<ReturnType<typeof buildFinanceFundFormSchema>>
export type FinanceFundFormInput = z.output<ReturnType<typeof buildFinanceFundFormSchema>>

export function createDefaultFinanceFundFormValues(): FinanceFundFormValues {
  return {
    name: "",
    description: "",
    targetAmount: "",
    startDate: "",
    endDate: "",
    spendingMode: FundSpendingMode.AnyExpense,
    allowedExpenseAccountIds: [],
    hasRestrictionPeriod: false,
    restrictionStartDate: "",
    restrictionEndDate: "",
  }
}

export function buildFinanceCategoryFormSchema(t: (key: string) => string) {
  return z.object({
    name: z.string().trim().min(2, t("validation.nameMin")).max(coreTextLimits.financeCategoryName, t("validation.nameMax")),
    description: z.string().trim().max(coreTextLimits.financeDescription, t("validation.descriptionMax")).or(z.literal("")),
    scope: z.nativeEnum(FinanceCategoryScope),
  })
}

export type FinanceCategoryFormValues = z.input<ReturnType<typeof buildFinanceCategoryFormSchema>>
export type FinanceCategoryFormInput = z.output<ReturnType<typeof buildFinanceCategoryFormSchema>>

export function createDefaultFinanceCategoryFormValues(scope: FinanceCategoryScope): FinanceCategoryFormValues {
  return {
    name: "",
    description: "",
    scope,
  }
}
